export const ANCESTRY_ARTICLES = [
  {
    slug: 'sign-in-ancestry-account-ftm',
    title: 'Connecting Your Ancestry Login to Family Tree Maker',
    excerpt:
      'A plain-English walkthrough for getting Family Tree Maker to accept your Ancestry username and password, plus fixes for stubborn login errors.',
    intro: [
      'Family Tree Maker works perfectly well by itself, but several of its best tools lean on an Ancestry connection. The leaf-shaped hint alerts, the built-in record search, and FamilySync all expect you to be logged in to an Ancestry account while you work in the program.',
      'When that login refuses to go through, the reason is rarely serious. It usually traces back to an old stored password, a program that has fallen behind on updates, a computer clock that is off, or security software being overly cautious. This guide walks through each possibility in a sensible order.',
    ],
    sections: [
      {
        heading: 'Before you start',
        paragraphs: ['Have these things ready so you are not hunting for them halfway through:'],
        bullets: [
          'The email address or username tied to your Ancestry account.',
          'Your current Ancestry password. If you are unsure of it, reset it on the Ancestry website before opening Family Tree Maker.',
          'The phone or inbox where Ancestry sends verification codes.',
          'A dependable internet connection. A cable connection or a strong Wi-Fi signal helps.',
        ],
      },
      {
        heading: 'Logging in from inside the program',
        steps: [
          'Open Family Tree Maker and click into the Web Search area, or kick off a sync. Either one brings up the login box.',
          'Type the same email and password that work for you on the Ancestry site. Watch out for Caps Lock and stray spaces.',
          'If Ancestry asks you to prove it is really you, enter the code it texts or emails you.',
          'Give the login box a moment to finish and disappear by itself. Clicking around while it is still working can cancel it.',
        ],
      },
      {
        heading: 'Why logins fail',
        paragraphs: [
          'Ancestry uses secure, encrypted connections for logins. Anything that interferes with that secure handshake can make a perfectly good password look wrong. A computer clock set to the wrong day, an outdated copy of Family Tree Maker, or an antivirus program that inspects web traffic can all cause this.',
          'Two-step verification is another common stumbling block. If the code arrives after the window has timed out, simply start the login again and use the newest code you receive.',
        ],
      },
      {
        heading: 'Fixes to try, one at a time',
        steps: [
          'Visit the Ancestry website in your browser and log in there. If that fails too, the problem lies with the password or account rather than Family Tree Maker.',
          'Look at the date, time, and time zone on your computer, and correct them if they are off.',
          'Install any available Family Tree Maker updates, then reopen the program.',
          'Temporarily allow Family Tree Maker in your firewall or antivirus settings.',
          'Disconnect from any VPN or proxy service while you log in. You can reconnect afterward.',
          'Restart the computer to clear out anything stuck in memory, then try once more.',
        ],
      },
      {
        heading: 'Common mistakes to avoid',
        bullets: [
          'Typing an old password that your browser has since replaced with a newer saved one.',
          'Using a different Ancestry account from the one your tree was originally linked to.',
          'Closing the login window before the verification step has finished.',
          'Trying over and over in quick succession. Too many failed attempts can briefly lock the account, so take a break if you have tried several times.',
        ],
      },
      {
        heading: 'Sharing one computer with family',
        paragraphs: [
          'If more than one person uses the same computer, have each person log out when they are done. Look for a sign-out choice in the program\'s menus, usually near the account or Help options.',
          'Keep in mind that a synced tree belongs to the Ancestry account that created the link. Someone else\'s login cannot take over syncing for that tree unless the link is removed and set up again under their account.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Do I have to pay for Ancestry to use Family Tree Maker?',
        a: 'No. You can build and edit trees, print charts, and make backups without any Ancestry membership. A paid membership only matters when you want to open certain Ancestry records or hints.',
      },
      {
        q: 'Why does it say my password is wrong when it works on the website?',
        a: 'This usually points to a secure-connection issue rather than the password itself. Check your computer clock, update Family Tree Maker, and briefly allow the program through your security software.',
      },
      {
        q: 'Will I have to log in every time I open the program?',
        a: 'Family Tree Maker generally remembers your login between sessions, but Ancestry may ask you to sign in again now and then for security reasons. That is normal and nothing to worry about.',
      },
      {
        q: 'Who can help with a billing question about my Ancestry membership?',
        a: 'Billing and membership matters are handled by Ancestry itself. Please reach out to them through their website for anything involving charges or renewals.',
      },
    ],
  },
  {
    slug: 'fix-familysync-errors',
    title: 'Getting a Stuck or Failing FamilySync Back on Track',
    excerpt:
      'Practical fixes for FamilySync error messages, syncs that never finish, missing changes, and doubled-up people between Family Tree Maker and Ancestry.',
    intro: [
      'FamilySync is the bridge that carries your edits back and forth between the tree on your computer and its partner tree on Ancestry. Each Family Tree Maker tree can be paired with one Ancestry tree, and the two are meant to mirror each other after every sync.',
      'Trouble tends to show up in a few recognizable ways: an error box you do not understand, a progress bar that crawls and never reaches the end, or a new photo or fact that appears in one place but not the other. The good news is that most of these can be sorted out without losing any research.',
    ],
    sections: [
      {
        heading: 'Protect your research first',
        paragraphs: [
          'Before changing anything, save a full backup of the tree to a USB drive or a separate folder. If a fix goes sideways, that backup lets you return to exactly where you were.',
          'Include media in the backup if you have attached photos or documents, so nothing has to be tracked down again later.',
        ],
      },
      {
        heading: 'Why syncs go wrong',
        bullets: [
          'Very large photos and scanned documents can time out on slower internet connections.',
          'Odd symbols or extremely long text in a name, place, or note may be refused by the online tree.',
          'Changing the same person on Ancestry and on your computer before syncing leaves the program unsure which version is right.',
          'Firewalls and antivirus tools sometimes treat sync traffic as suspicious and block it.',
          'An expired Ancestry login can quietly stop the sync from connecting.',
        ],
      },
      {
        heading: 'Start with the simple checks',
        steps: [
          'Open a web browser and confirm your Ancestry login works there.',
          'Update Family Tree Maker to the newest release offered for your version.',
          'Reboot the computer so the program starts fresh.',
          'Connect to reliable internet, ideally not a public or hotel network, and run the sync again.',
          'If it still seems to hang, leave it running for a while. Big trees with lots of media can take much longer than you might expect.',
        ],
      },
      {
        heading: 'Tracking down a problem record',
        paragraphs: [
          'If an error message names a specific person, source, or media item, open that record and look for anything unusual. Trim very long notes, remove special characters from names and places, and check that attached files still exist on your computer. Then sync again.',
          'If a single photo keeps failing, try detaching it, syncing, and adding it back afterward.',
        ],
      },
      {
        heading: 'Starting over with a fresh link',
        paragraphs: [
          'When a sync keeps breaking no matter what you try, removing the link and building a new one is often the most reliable cure. First decide which version you trust more, the one on your computer or the one online. That version becomes the foundation of the new link.',
          'After relinking you may end up with an extra online tree. Leave it alone until you have compared the new tree carefully, and hang on to your backup until you are fully satisfied.',
        ],
      },
      {
        heading: 'Habits that prevent future sync trouble',
        bullets: [
          'Pick one place to do most of your editing, either on your computer or online, and sync often.',
          'Sync at the end of each research session instead of letting changes pile up for weeks.',
          'Keep Family Tree Maker updated.',
          'Shrink enormous scans to a reasonable size before attaching them.',
        ],
      },
    ],
    faqs: [
      {
        q: 'If I unlink my tree, will the online copy disappear?',
        a: 'No. Unlinking just breaks the connection between the two trees. Both stay where they are, and you can decide later whether to keep, rename, or remove the online one.',
      },
      {
        q: 'Why do I suddenly see the same person twice?',
        a: 'Duplicates often appear when one individual was added separately on the website and in the desktop program between syncs. After confirming they really are the same person, merge them carefully in Family Tree Maker and sync again.',
      },
      {
        q: 'Can one desktop tree sync with two Ancestry trees?',
        a: 'No. FamilySync pairs one Family Tree Maker tree with a single Ancestry tree. If you need a second online copy, you would have to make a separate desktop tree for it.',
      },
      {
        q: 'How long should a sync take?',
        a: 'It depends on the size of the tree, how much media it holds, and your internet speed. A small change may take moments, while a first sync of a large tree with many photos can take a long time.',
      },
    ],
  },
  {
    slug: 'download-ancestry-tree-to-ftm',
    title: 'Moving Your Online Ancestry Tree onto Your Computer',
    excerpt:
      'Copy a tree you built on Ancestry into Family Tree Maker, with its people, sources, and pictures, and decide whether the two should stay linked.',
    intro: [
      'Plenty of family historians begin on the Ancestry website and later want the comfort of a copy that lives on their own computer. Family Tree Maker can pull that online tree down, complete with individuals, source citations, and, if you choose, the attached photos and documents.',
      'Partway through, you will be asked whether the new desktop tree should stay synced with the online one. Either answer is fine. Syncing keeps both versions matched, while an unlinked download gives you an independent copy to work on privately.',
    ],
    sections: [
      {
        heading: 'Before you start',
        bullets: [
          'Log in to the Ancestry website once to make sure your password works.',
          'Confirm there is plenty of free space on your computer, especially if the tree has many pictures.',
          'Plan the download for a time when you will not need the computer for a while. Large trees can take some time.',
          'Install any pending Family Tree Maker updates.',
        ],
      },
      {
        heading: 'Downloading the tree',
        steps: [
          'Launch Family Tree Maker and select the option for a new tree.',
          'Pick the choice that brings in a tree from Ancestry.',
          'Log in when asked, then select the tree you want from the list shown for your account.',
          'Decide whether this copy should stay linked for syncing.',
          'Choose whether to include media. It adds time, but it means your photos can be viewed even without internet.',
          'Leave the program open and let the download finish completely.',
        ],
      },
      {
        heading: 'Checking your new copy',
        paragraphs: [
          'Once the download ends, take a few minutes to look around before doing any new work. Open several branches, including a distant one, and compare names, dates, and places against what you see online.',
        ],
        bullets: [
          'Open the Media area to confirm pictures and documents came through.',
          'Look at a couple of source citations to make sure they are attached.',
          'Save a backup immediately so you have an untouched starting point.',
        ],
      },
      {
        heading: 'Why some items may be missing',
        paragraphs: [
          'Not everything online arrives in the same form. Record images from paid Ancestry collections may need an active membership to view in full. Media you chose not to download will not appear on your computer either, and some items added by other members may not come across.',
        ],
      },
      {
        heading: 'What to do if the download fails',
        steps: [
          'Reboot your computer, then give it another go on a steady connection.',
          'Try the download without media first. Pictures can often be brought down afterward.',
          'Make sure your security software is not blocking the program.',
          'If the same tree fails repeatedly, give us a call and we can walk through other options with you, such as working from a GEDCOM file.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Is it possible to download a tree owned by another person?',
        a: 'Only when the owner has given you enough access. If you cannot see it in your list, ask the owner to raise your permission level or to email you a GEDCOM file you can open in Family Tree Maker.',
      },
      {
        q: 'Will downloading remove the tree from Ancestry?',
        a: 'No. Downloading makes a copy. The online tree stays exactly where it was.',
      },
      {
        q: 'Should I choose to keep the trees synced?',
        a: 'If you like to work both online and on your computer, syncing keeps the two in step. If you prefer to work only on your computer, an unlinked copy is simpler and avoids sync conflicts.',
      },
      {
        q: 'I downloaded the tree twice by accident. What now?',
        a: 'Keep the copy that looks complete, back it up, and remove the extra one from your computer once you are certain you do not need it.',
      },
    ],
  },
  {
    slug: 'cancel-ancestry-keep-family-tree',
    title: 'Ending an Ancestry Membership Without Losing Your Research',
    excerpt:
      'A clear look at what stays and what goes when a paid Ancestry membership stops, and the simple steps that keep your tree safe.',
    intro: [
      'Many people put off cancelling a paid Ancestry membership because they fear years of work will vanish. That worry is understandable, but stopping a subscription and erasing a tree are two very different things.',
      'The tree file that Family Tree Maker keeps on your computer does not depend on an Ancestry membership. Your online tree also normally remains in your account after the paid features end. What changes is mostly access to records and certain hints. The steps below help you walk away with every bit of your work protected.',
    ],
    sections: [
      {
        heading: 'What remains yours',
        bullets: [
          'The Family Tree Maker file on your computer, along with any photos and documents you downloaded into it.',
          'Typically, the online tree itself, which stays attached to your account even without a paid plan.',
          'All the names, dates, stories, and pictures you typed in or uploaded yourself.',
          'Any backups or GEDCOM files you have saved.',
        ],
      },
      {
        heading: 'What you may lose access to',
        bullets: [
          'Record collections and hints that are reserved for paying members.',
          'Full-size views of paid-collection record images linked to your people.',
          'Some research features on the website that depend on a membership.',
        ],
      },
      {
        heading: 'Why the desktop tree is safe',
        paragraphs: [
          'Family Tree Maker saves your tree as a file on your own hard drive. Ancestry cannot reach in and remove it. Even if you never renew, the program will keep opening that file and let you edit, print charts, and make backups just as before.',
        ],
      },
      {
        heading: 'Checklist before you cancel',
        steps: [
          'Run a final sync so your computer and the website hold the same information.',
          'Download any record images you care about into your tree while you still have access.',
          'Make a full Family Tree Maker backup and include media.',
          'Save a GEDCOM export as a universal copy that other genealogy programs can open.',
          'Keep a copy of your backup somewhere other than the computer, such as a USB drive or cloud storage.',
        ],
      },
      {
        heading: 'Cancelling a membership versus closing an account',
        paragraphs: [
          'These are not the same thing. Cancelling a membership stops future charges and paid features. Closing or deleting the whole Ancestry account goes much further and removes the online trees tied to it. Your desktop file would still be fine, but anything that existed only online would be gone.',
          'If all you want is to stop paying, cancelling the membership is usually enough.',
        ],
      },
      {
        heading: 'Who to contact about billing',
        paragraphs: [
          'We are an independent support service and cannot see or change your Ancestry billing. For questions about charges, refunds, or renewal dates, please contact Ancestry directly through their website.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Does closing my entire Ancestry account work differently from cancelling?',
        a: 'Yes. Closing the account removes the online trees attached to it. The Family Tree Maker file on your computer is unaffected, but it is wise to make a fresh backup before taking that step.',
      },
      {
        q: 'Can I still sync after my membership ends?',
        a: 'In many cases syncing can continue with a free account, although record-related features will be limited. If syncing does stop, your desktop tree is still complete and usable.',
      },
      {
        q: 'Will my hints disappear?',
        a: 'Hints tied to paid collections may no longer open fully, while hints from free records may still be available. Anything you already saved into your tree stays there.',
      },
      {
        q: 'If I rejoin later, will my tree still be there?',
        a: 'Your desktop tree certainly will be. If you did not delete your online tree or close your account, it should normally still be in your account as well.',
      },
    ],
  },
  {
    slug: 'delete-family-tree-on-ancestry',
    title: 'Removing an Unwanted Online Tree from Ancestry',
    excerpt:
      'Clear away a test tree or an accidental duplicate on Ancestry the safe way, and understand how it affects the copy in Family Tree Maker.',
    intro: [
      'Over time it is easy to collect extra online trees: a practice tree from your early days, a copy created while relinking FamilySync, or a tree started for a relative who never used it. Tidying these up makes your account easier to manage.',
      'There is one important catch. Once an online tree is deleted, Ancestry does not offer a simple way to bring it back. Taking a few minutes to confirm you have a safe local copy first will spare you a very unpleasant surprise.',
    ],
    sections: [
      {
        heading: 'Reasons people remove a tree',
        bullets: [
          'A test tree made while learning is no longer needed.',
          'A relink or repeated upload left behind duplicate trees.',
          'You want to start fresh with a cleaner version.',
          'You no longer wish to keep certain information online.',
        ],
      },
      {
        heading: 'Before you start',
        steps: [
          'Double-check which tree you are about to remove. Tree names can look very similar.',
          'Make sure the information exists somewhere else, such as a Family Tree Maker file or a GEDCOM export.',
          'If a desktop tree is linked to this online tree, remove that link in Family Tree Maker first.',
          'Save a fresh backup of your desktop tree and confirm that it opens.',
        ],
      },
      {
        heading: 'Deleting the tree on the website',
        steps: [
          'Open the Ancestry website in your browser and log in.',
          'Navigate to the tree that needs removing.',
          'Open the settings area for that particular tree.',
          'Find the delete option and follow the prompts. You may be asked to type the tree name as a final confirmation.',
        ],
      },
      {
        heading: 'Common mistakes to avoid',
        bullets: [
          'Deleting a tree that is still linked to your main desktop tree.',
          'Choosing the wrong tree because two have almost identical names.',
          'Assuming a backup exists without actually checking that it opens.',
          'Forgetting that relatives you invited to the tree will lose access too.',
        ],
      },
      {
        heading: 'Life after the online tree is gone',
        paragraphs: [
          'Your Family Tree Maker file keeps working exactly as before. It simply no longer has an online partner. If you later want an online copy again, you can set up a brand-new link, which places the tree into a freshly created online tree.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Is there any way to undo deleting an online tree?',
        a: 'In general, no. Treat deletion as permanent, which is exactly why a tested backup of your desktop tree is so valuable.',
      },
      {
        q: 'Will deleting the online tree remove it from Family Tree Maker?',
        a: 'No. The desktop file on your computer is separate, and it will keep opening normally after the online tree is removed.',
      },
      {
        q: 'What happens to people I invited to view the tree?',
        a: 'They lose access once the tree is deleted. If they still want the information, share a GEDCOM file or a printed chart with them beforehand.',
      },
      {
        q: 'Can I hide a tree instead of deleting it?',
        a: 'Ancestry offers privacy choices for trees, so making a tree private may be an option instead of removing it. Look through the tree settings on the website to see what is available to you.',
      },
    ],
  },
  {
    slug: 'familysearch-login-in-ftm',
    title: 'Linking a Free FamilySearch Account to Family Tree Maker',
    excerpt:
      'Set up the FamilySearch connection in Family Tree Maker, compare your relatives with the shared FamilySearch tree, and fix login hiccups.',
    intro: [
      'FamilySearch is a large, free genealogy website with a shared, worldwide family tree. Family Tree Maker can talk to it directly, so you can line up people in your own tree with matching people on FamilySearch and copy useful facts and sources back into your file.',
      'To use this feature you need a FamilySearch account, which costs nothing to create. Once it is connected, you can work through your tree one person at a time. If the login window misbehaves, a few quick checks usually get it working again.',
    ],
    sections: [
      {
        heading: 'Before you start',
        bullets: [
          'If you have no FamilySearch account yet, sign up for one at no cost on the FamilySearch website, then confirm your login works there.',
          'Update Family Tree Maker so the connection features are current.',
          'Keep your FamilySearch username and password written down somewhere safe.',
        ],
      },
      {
        heading: 'Making the connection',
        steps: [
          'Open your tree in Family Tree Maker and pick a person to work with.',
          'Look for the FamilySearch tab or FamilySearch tools for that person.',
          'When the FamilySearch login window pops up, enter your details.',
          'Approve the request that lets Family Tree Maker connect to your account.',
        ],
      },
      {
        heading: 'Matching relatives the careful way',
        paragraphs: [
          'Take one person at a time. Compare birth dates, places, spouses, and children before deciding that a FamilySearch person truly is the same as yours.',
          'Because the FamilySearch tree is shared by a huge community, anyone can edit it. That makes it a wonderful collaboration tool, but it also means mistakes can creep in. Bring over only details you are comfortable with, and favor facts that have sources attached.',
        ],
      },
      {
        heading: 'Why the login window may not work',
        paragraphs: [
          'The FamilySearch login appears in a small window inside Family Tree Maker. Pop-up blockers, strict security software, or an out-of-date program can keep that window from loading or finishing.',
        ],
      },
      {
        heading: 'Things to try if you cannot log in',
        steps: [
          'Log out of FamilySearch within Family Tree Maker, then log in again to refresh the stored details.',
          'Install the newest Family Tree Maker update.',
          'Temporarily relax any pop-up blocking or security settings that might be stopping the window.',
          'Confirm that your login works on the FamilySearch website itself.',
          'Reboot your computer and give it one more attempt.',
        ],
      },
      {
        heading: 'Protecting your research',
        bullets: [
          'Save a backup of your tree ahead of any big session of copying information from FamilySearch.',
          'Add notes explaining where new facts came from.',
          'Resist merging people just because the names match. Common names can fool anyone.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Is there a charge to use FamilySearch?',
        a: 'No. Creating an account is free, and a large number of records on the site can be viewed without paying.',
      },
      {
        q: 'Is an Ancestry account required for the FamilySearch features?',
        a: 'No. The FamilySearch connection uses your FamilySearch account, which is completely separate from Ancestry.',
      },
      {
        q: 'Will my Family Tree Maker edits show up on FamilySearch automatically?',
        a: 'Generally, information is only exchanged when you choose to do so. Keep in mind that anything you do add to the shared FamilySearch tree can be seen and edited by other users.',
      },
      {
        q: 'Why does the program keep asking me to sign in again?',
        a: 'Saved login details can expire. Logging out completely and then back in usually settles it, and updating Family Tree Maker can help as well.',
      },
    ],
  },
  {
    slug: 'create-new-tree-familysearch',
    title: 'Building a Brand-New Tree with Help from FamilySearch',
    excerpt:
      'Start a fresh Family Tree Maker file with the relatives you know, then use the free FamilySearch Family Tree to reach further back, generation by generation.',
    intro: [
      'If you are just beginning your family history, there is no need to type in every ancestor by hand. Start a new Family Tree Maker file with the people you know well, such as you, your mom and dad, and their parents, and then use FamilySearch to explore the generations behind them.',
      'FamilySearch can be a quick way to grow a tree, but speed is not the goal. A smaller tree with reliable facts is far more valuable than a huge one full of guesses. This guide shows how to build steadily and check your work along the way.',
    ],
    sections: [
      {
        heading: 'Before you start',
        bullets: [
          'Gather what the family already knows: full names, birth and marriage dates, places, and any old documents or photos.',
          'Set up a free FamilySearch account and make sure you can log in.',
          'Talk to older relatives if you can. Their memories will help you confirm matches later.',
        ],
      },
      {
        heading: 'Laying the foundation',
        steps: [
          'In Family Tree Maker, start a new, empty tree.',
          'Enter yourself first, then add your mother, father, and grandparents with whatever details you have.',
          'Log in to FamilySearch from within Family Tree Maker.',
          'Starting with one grandparent, look for the matching person on FamilySearch and compare the details closely.',
          'When you are confident of a match, look over the parents FamilySearch lists for that grandparent.',
        ],
      },
      {
        heading: 'Growing the tree one step at a time',
        paragraphs: [
          'Add a single generation, then pause. Look at which sources are attached to each new person and read them if you can. Only move on once the newcomers make sense alongside what you already know.',
          'It is tempting to pull in dozens or even hundreds of relatives in a single evening. Resist that urge. Mistakes made early tend to multiply the further back you go.',
        ],
      },
      {
        heading: 'Common mistakes to avoid',
        bullets: [
          'Accepting a match because the name is right even though the dates or places do not fit.',
          'Copying ancestors who have no sources at all.',
          'Skipping backups while the tree is growing quickly.',
          'Confusing two people who shared the same name and lived in the same town.',
        ],
      },
      {
        heading: 'Quick checklist for each new ancestor',
        bullets: [
          'Do the birth and death dates make sense next to their parents and children?',
          'Is at least one source attached?',
          'Does the location fit what you know about where the family lived?',
          'Have you noted where the information came from?',
        ],
      },
    ],
    faqs: [
      {
        q: 'Is everything on FamilySearch accurate?',
        a: 'Not always. Think of it as a set of leads. Check whether sources are attached, and weigh them against records you find on your own before treating anything as settled.',
      },
      {
        q: 'How far back should I go with FamilySearch?',
        a: 'Go as far as the evidence supports. When sources become thin or start to conflict, that is a sign to slow down and dig deeper before adding more generations.',
      },
      {
        q: 'Can I add FamilySearch people to a tree I already have?',
        a: 'Yes. You do not have to start a new file. The same matching tools work on people in an existing tree.',
      },
      {
        q: 'Should I make backups while building the tree?',
        a: 'Yes. Save a backup after each productive session. If you later spot a wrong branch, you can return to an earlier version instead of untangling it by hand.',
      },
    ],
  },
  {
    slug: 'treevault-cloud-services-guide',
    title: 'Understanding TreeVault: Cloud Storage for Your Family Tree Maker Files',
    excerpt:
      'Learn what TreeVault does, how to put a tree into it, how to bring a tree back after a computer problem, and how to handle common hiccups.',
    intro: [
      'TreeVault is an online service run by the publisher of Family Tree Maker for people who use the program. It keeps a copy of your tree on remote servers, so a failed hard drive, a stolen laptop, or a move to a new computer does not have to mean starting over. It can also make it easier to share a tree with family.',
      'Think of TreeVault as an extra layer of safety rather than your only protection. Used alongside regular backups on a USB drive, it gives you two independent copies kept in two different places.',
    ],
    sections: [
      {
        heading: 'Before you start',
        bullets: [
          'Check that your version of Family Tree Maker includes TreeVault support. Older versions may not.',
          'Have the email and password for your Family Tree Maker account handy.',
          'Make a regular backup of your tree first, just in case.',
          'Plan to upload on a steady internet connection, since large trees with many photos take time.',
        ],
      },
      {
        heading: 'Placing a tree in TreeVault',
        steps: [
          'Open Family Tree Maker and log in to your Family Tree Maker account inside the program.',
          'Select the tree that should be stored online.',
          'Start the upload to TreeVault.',
          'Keep the program running until the upload reports that it is complete. Closing early can leave an unfinished copy.',
        ],
      },
      {
        heading: 'Bringing a tree back after a computer problem',
        steps: [
          'Install Family Tree Maker on the working or replacement computer.',
          'Log in with the same Family Tree Maker account you used for TreeVault.',
          'Find your stored tree and download it.',
          'Check when the stored copy was saved to see how recent it is, then open the tree and look over your latest work.',
        ],
      },
      {
        heading: 'Why an older version sometimes comes back',
        paragraphs: [
          'TreeVault can only return what was last uploaded. If you did a lot of research after the most recent upload, those changes will not be in the stored copy. Uploading after every major session keeps that gap small.',
        ],
      },
      {
        heading: 'Solving common TreeVault hiccups',
        bullets: [
          'Upload seems stuck: large media collections take time, so switch to a faster connection if possible and let the program keep running.',
          'Cannot log in: confirm you are using the right email for your Family Tree Maker account, and reset the password if needed.',
          'Tree not listed: make sure you are logged in with the same account you used to upload it.',
          'Restored tree looks out of date: compare it with your local backups and use whichever is newest.',
        ],
      },
      {
        heading: 'A simple backup routine',
        bullets: [
          'After each research session, save a local backup.',
          'Once a week, or after big changes, upload to TreeVault.',
          'Every few months, copy a backup to a USB drive kept somewhere other than your desk.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Is TreeVault a replacement for normal backups?',
        a: 'No. It works best alongside them. With one copy on a USB stick and a second in TreeVault, a single problem cannot wipe out both.',
      },
      {
        q: 'Is TreeVault part of Ancestry?',
        a: 'No. TreeVault is provided by the publisher of Family Tree Maker and is tied to your Family Tree Maker account rather than an Ancestry login.',
      },
      {
        q: 'Does TreeVault cost extra?',
        a: 'Availability and pricing can depend on your version and the publisher\'s current offers. For account or billing questions, please contact the publisher directly.',
      },
      {
        q: 'Can relatives see my tree in TreeVault?',
        a: 'Only if you choose to share it. Review the sharing options in your version of Family Tree Maker, and share only with people you trust.',
      },
    ],
  },
];
